import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";

import { Box, Button, ButtonGroup, Checkbox, CloseButton, Dialog, Heading, HStack, NumberInput, Popover, Portal, Text, VStack } from "@chakra-ui/react";
import { LuPlus, LuRedo, LuSaveAll, LuSendToBack, LuSpline, LuSquareSplitHorizontal, LuTrash, LuUndo, LuX, LuMerge, LuTangent, LuRectangleHorizontal, LuBot } from "react-icons/lu";
import HoParallelLine from "../../../../../icons/HoParallelLine";

import Type from "../../../../../utilies/Types";
import { Tooltip } from "../../../../ui/tooltip";

import { cloneEntity, getCombinedKeysText, getGeometryTypeGeojson, getUsernameAndUserIdFormat, normalizeGeometryType, removeFileExtension, showToast } from "../../../../../utilies/Utility";

import { cancelGeojsonEdit, enableReverseOnClick, deleteSelectedEntities, createCopyPasteManager } from "../../../../../utilies/EditingGeojsonUtility";
import { getEditedGeoJSON } from "../../../../../utilies/geojson/GetEditedGeojson";
import { CreateEntityOnQuadraticBezier } from "../../../../../utilies/editing/CreateEntityOnQuadraticBezier";

import LayerEditConfirmCancelDialog from "./LayerEditConfirmCancelDialog";
import LayersEditPropertiesPopup from "./LayersEditPropertiesPopup";
import { offsetSelectedPolylineOnce } from "../../../../../utilies/editing/Parallel";
import { drawingNewEntity } from "../../../../../utilies/editing/DrawingNewEntity";
import { enableGeojsonEditInteractions } from "../../../../../utilies/editing/EditInteractions";
import { handleCuttingGeojsonLines } from "../../../../../utilies/editing/Spliting";
import { toggleDirectionalArrows } from "../../../../../utilies/geojson/Arrow";
import { joinTwoSelectedPolylinesOnce } from "../../../../../utilies/editing/JoinEntities";
import { createMultiSelectHandler, resetEntityStyle } from "../../../../../utilies/other/MultiSelection";
import { Color, ColorMaterialProperty, GeoJsonDataSource, JulianDate, PolylineDashMaterialProperty } from "cesium";

import { restoreCameraControls } from "../../../../../utilies/MapUtility";
import { CreateEntityOnCubicBezier } from "../../../../../utilies/editing/CreateEntityOnCubicBezier";
import { useAppStore } from "../../../../../context/store";
import { useMapObjects } from "../../../../../context/MapObjectsContext";
import { buildBoxPolygonFromCenterLine, drawBoxCenterLineOnce } from "../../../../../utilies/editing/BoxFromCenterLineTool";
import { createDigitizerAssistant } from "../../../../../utilies/editing/DigitizerAssistant";
import { createToggleAssetVisibility } from "../../../../../utilies/other/ToggleAssetVisibility";

const LayersEdit = () => {
  // ===== state =====
  const editType = useAppStore((s) => s.editType);
  const editMode = useAppStore((s) => s.editMode);
  const { mapObjects, setMapObjects, mapObjectsRef } = useMapObjects();
  const currentLayerEditing = useAppStore((s) => s.currentLayerEditing);
  const snappingOptions = useAppStore((s) => s.snappingOptions);
  const showApprovalEditDialog = useAppStore((s) => s.showApprovalEditDialog);
  const lineSampleRate = useAppStore((s) => s.lineSampleRate);
  const settings = useAppStore((s) => s.settings);
  const userData = useAppStore((s) => s.userData);

  // ===== refs / objects =====
  const viewerRef = useAppStore.getState().viewerRef;
  const featurePointMapping = useAppStore.getState().featurePointMapping;
  const originalCameraControls = useAppStore.getState().originalCameraControls;
  const controlPointEntities = useAppStore.getState().controlPointEntities;
  const editTypeRef = useAppStore.getState().editTypeRef;
  const undoStackRef = useAppStore.getState().undoStackRef;
  const editHandler = useAppStore.getState().editHandler;
  const selectedEntitiesRef = useAppStore.getState().selectedEntitiesRef;
  const directionArrowSettingRef = useAppStore.getState().directionArrowSettingRef;
  const highlightToolsRef = useAppStore.getState().highlightToolsRef;
  const savedHotkeysRef = useAppStore.getState().savedHotkeysRef;
  const lineSampleRateRef = useAppStore.getState().lineSampleRateRef;
  const autoJumpEnabledRef = useRef(true);

  const savedPropsInNewEntity = useAppStore((s) => s.savedPropsInNewEntity);
  const savedPropsInNewEntityLayerKey = useAppStore((s) => s.savedPropsInNewEntityLayerKey);
  const setSavedPropsInNewEntityLayerKey = useAppStore.getState().setSavedPropsInNewEntityLayerKey;

  const toggleAssetVisibility = useMemo(() => {
    return createToggleAssetVisibility({
      viewerRef,
      setMapObjects,
    });
  }, [viewerRef, setMapObjects]);

  // ===== setters =====
  const setEditMode = useAppStore.getState().setEditMode;
  const setEditType = useAppStore.getState().setEditType;
  const setCurrentLayerEditing = useAppStore.getState().setCurrentLayerEditing;
  const setCursorMode = useAppStore.getState().setCursorMode;
  const setUndoStack = useAppStore.getState().setUndoStack;
  const setRedoStack = useAppStore.getState().setRedoStack;
  const setEditHandler = useAppStore.getState().setEditHandler;
  const setAssociation = useAppStore.getState().setAssociation;
  const setGenerateArc = useAppStore.getState().setGenerateArc;
  const setCreateLaneletMode = useAppStore.getState().setCreateLaneletMode;
  const setOverlayTextMode = useAppStore.getState().setOverlayTextMode;
  const setCanHightlightEntity = useAppStore.getState().setCanHightlightEntity;
  const setShowApprovalEditDialog = useAppStore.getState().setShowApprovalEditDialog;
  const setLineSampleRate = useAppStore.getState().setLineSampleRate;
  const infoboxRef = useAppStore((s) => s.infoboxRef);
  // ===== undo / redo actions =====
  const pushUndo = useAppStore.getState().pushUndo;
  const pushRedo = useAppStore.getState().pushRedo;
  const popUndo = useAppStore.getState().popUndo;
  const popRedo = useAppStore.getState().popRedo;

  const setSavedPropsInNewEntity = useAppStore.getState().setSavedPropsInNewEntity;

  const preEditGeojsonRef = useRef(null);
  const preEditLayerIdRef = useRef(null);
  const prevLayerEditingRef = useRef(null);

  const [isOpenParallelDialog, setIsOpenParallelDialog] = useState(false);
  const [isOpenFeatureTypeDialog, setIsOpenFeatureTypeDialog] = useState(false);
  const [newGeojson, setNewGeojson] = useState(null);
  const [splitHandler, setSplitHandler] = useState(null);
  const [reverseHandler, setReverseHandler] = useState(null);
  const [deleteHandler, setDeleteHandler] = useState(null);
  const [openPropertiesDialog, setOpenPropertiesDialog] = useState(false);
  const drawHandlerRef = useRef(null);
  const maxIdRef = useRef(null);
  const entityIdRef = useRef(null);
  const [activeArc, setActiveArc] = useState(false);
  const [activeArc3, setActiveArc3] = useState(false);
  const [parallelDistance, setParallelDistance] = useState(0.2);
  const [parallelDirection, setParallelDirection] = useState("left");
  const drawSessionRef = useRef(null);
  const autoSaveNewRef = useRef(false);
  const restartNewRef = useRef(false);

  const [selectionHandler, setSelectionHandler] = useState(null);
  const [isShowConfirmCancelDialog, setIsShowConfirmCancelDialog] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const [newFeatureType, setNewFeatureType] = useState("Point");

  const [joinHandler, setJoinHandler] = useState(null);

  const TOOL = useMemo(
    () => ({
      NONE: "none",
      REVERSE: "reverse",
      SPLIT: "split",
      NEW: "new",
      ARC: "arc",
      ARC_CUBIC: "arc3",
      PARALLEL: "parallel",
      JOIN: "join",
      BOX: "box",
      DIGITIZER_ASSISTANT: "digitizerAssistant",
    }),
    [],
  );

  const [isOpenDigitizerDialog, setIsOpenDigitizerDialog] = useState(false);

  const [digitizerMinDistance, setDigitizerMinDistance] = useState(3);
  const [digitizerMaxDistance, setDigitizerMaxDistance] = useState(11.8);
  const digitizerMinDistanceRef = useRef(3);
  const digitizerMaxDistanceRef = useRef(11.8);

  const digitizerDurationRef = useRef(500);

  const digitizerCtlRef = useRef(null);

  const [activeTool, setActiveTool] = useState(TOOL.NONE);

  const [activeBox, setActiveBox] = useState(false);
  const [isOpenBoxWidthDialog, setIsOpenBoxWidthDialog] = useState(false);
  const [boxWidth, setBoxWidth] = useState(2);
  const [boxCenterLine, setBoxCenterLine] = useState(null);
  const [boxPreviewLength, setBoxPreviewLength] = useState(0);
  const boxPromiseRef = useRef(null);

  const activeToolRef = useRef(activeTool);

  useEffect(() => {
    activeToolRef.current = activeTool;
    editTypeRef.current = editType;
  }, [activeTool, editType]);

  const activeArcRef = useRef(activeArc);
  const activeArc3Ref = useRef(activeArc3);

  useEffect(() => {
    activeArcRef.current = activeArc;
    activeArc3Ref.current = activeArc3;
  }, [activeArc, activeArc3]);

  const canStartTool = useCallback(
    (toolName) => {
      // If some other tool is active, block the switch and warn user
      if (activeTool !== TOOL.NONE && activeTool !== toolName) {
        showToast(`Finish or cancel the '${activeTool}' tool first.`, Type.NOTIFICATION.WARNING);
        return false;
      }
      return true;
    },
    [activeTool, TOOL],
  );

  const lockedTo = useCallback((toolName) => activeTool !== TOOL.NONE && activeTool !== toolName, [activeTool, TOOL]);

  const handlersRef = useRef({ sel: null, edit: null, split: null, del: null, rev: null, join: null });

  useEffect(() => {
    handlersRef.current.sel = selectionHandler;
  }, [selectionHandler]);
  useEffect(() => {
    handlersRef.current.edit = editHandler;
  }, [editHandler]);
  useEffect(() => {
    handlersRef.current.split = splitHandler;
  }, [splitHandler]);
  useEffect(() => {
    handlersRef.current.del = deleteHandler;
  }, [deleteHandler]);
  useEffect(() => {
    handlersRef.current.rev = reverseHandler;
  }, [reverseHandler]);
  useEffect(() => {
    handlersRef.current.join = joinHandler;
  }, [joinHandler]);

  // Unmount cleanup: destroy any active handlers and cancel edit overlays
  useEffect(() => {
    return () => {
      const { sel, edit, split, del, rev } = handlersRef.current || {};
      try {
        sel?.destroy?.();
      } catch {}
      try {
        edit?.destroy?.();
      } catch {}
      try {
        split?.destroy?.();
      } catch {}
      try {
        del?.destroy?.();
      } catch {}
      try {
        rev?.destroy?.();
      } catch {}
      try {
        handlersRef.current.join?.destroy?.();
      } catch {}
      try {
        cancelGeojsonEdit(viewerRef.current, controlPointEntities, featurePointMapping, originalCameraControls);
      } catch {}

      if (arcPromiseRef.current?.cancel) {
        arcPromiseRef.current.cancel();
        arcPromiseRef.current = null;
      }

      if (boxPromiseRef.current?.cancel) {
        boxPromiseRef.current.cancel();
        boxPromiseRef.current = null;
      }

      if (parallelPromiseRef.current?.cancel) {
        parallelPromiseRef.current.cancel();
        parallelPromiseRef.current = null;
      }

      // Ensure tool lock is released on unmount
      setActiveTool(TOOL.NONE);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize editing mode handlers when editMode toggles
  useEffect(() => {
    if (editMode) {
      if (currentLayerEditing?.id && preEditLayerIdRef.current !== currentLayerEditing.id) {
        preEditLayerIdRef.current = currentLayerEditing.id;
        preEditGeojsonRef.current = currentLayerEditing.geojson ? JSON.parse(JSON.stringify(getEditedGeoJSON(currentLayerEditing))) : null;
      }

      // clear any cross-connection overlays then create selection & edit handlers
      // Cancel association mode
      setAssociation(false);

      // reset styles
      highlightToolsRef.current?.();
      setCanHightlightEntity(false);

      // Close cesium info box
      infoboxRef.current?.close();

      setGenerateArc(false);
      setCreateLaneletMode(false);

      setSelectionHandler(createMultiSelectHandler(viewerRef.current, currentLayerEditing.dataSource, selectedEntitiesRef, setHasSelection));
      // Cancel overlay text mode
      setOverlayTextMode(false);
      setEditHandler(
        enableGeojsonEditInteractions(
          viewerRef.current,
          currentLayerEditing.dataSource,
          originalCameraControls,
          pushUndo,
          selectedEntitiesRef,
          currentLayerEditing,
          snappingOptions,
          editTypeRef,
          directionArrowSettingRef,
          settings,
        ),
      );
    } else if (selectionHandler) {
      preEditGeojsonRef.current = null;
      preEditLayerIdRef.current = null;

      // If leaving edit mode, destroy selection handler and clear selection
      selectionHandler.destroy();
      setSelectionHandler(null);
      selectedEntitiesRef.current = [];
      // Also clear any tool locks when leaving edit mode
      setActiveTool(TOOL.NONE);
      setIsOpenParallelDialog(false);
      setActiveArc(false);
      setCanHightlightEntity(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  // Force-cancel state refs: when user confirms forced cancel
  const [forceToCancel, setForceToCancel] = useState(false);
  const forceToCancelRef = useRef(forceToCancel);

  useEffect(() => {
    forceToCancelRef.current = forceToCancel;
    if (!forceToCancel) return;

    (async () => {
      await cancelEditing();

      setForceToCancel(false);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceToCancel]);

  useEffect(() => {
    if (!editMode) {
      cancelEditing();
    }
  }, [editMode]);

  const cancelEditing = async () => {
    if (!viewerRef?.current || viewerRef.current.isDestroyed?.()) return;

    try {
      if (undoStackRef.current.length > 0 && !forceToCancelRef.current) {
        setIsShowConfirmCancelDialog(true);
        return;
      }

      if (arcPromiseRef.current?.cancel) {
        arcPromiseRef.current.cancel();
        arcPromiseRef.current = null;
      }

      if (boxPromiseRef.current?.cancel) {
        boxPromiseRef.current.cancel();
        boxPromiseRef.current = null;
      }

      if (parallelPromiseRef.current?.cancel) {
        parallelPromiseRef.current.cancel();
        parallelPromiseRef.current = null;
      }

      setActiveBox(false);

      setIsOpenBoxWidthDialog(false);
      setBoxCenterLine(null);
      setBoxPreviewLength(0);

      setEditType("none");
      setEditMode(false);
      setCanHightlightEntity(true);
      setCursorMode(Type.CURSOR.DEFAULT);

      setActiveTool(TOOL.NONE);
      setIsOpenParallelDialog(false);
      setActiveArc(false);
      setActiveArc3(false);

      if (viewerRef.current && !viewerRef.current.isDestroyed?.()) {
        restoreCameraControls(viewerRef.current, originalCameraControls);
      }

      cancelDrawing();

      const h = handlersRef.current?.split || splitHandler;
      if (h && !h.isDestroyed?.()) {
        try {
          h.destroy?.();
        } catch {}
        setSplitHandler(null);
      }

      if (deleteHandler && !deleteHandler.isDestroyed?.()) {
        try {
          deleteHandler.destroy();
        } catch {}
        setDeleteHandler(null);
      }

      if (reverseHandler && !reverseHandler.isDestroyed?.()) {
        try {
          reverseHandler.destroy();
        } catch {}
        setReverseHandler(null);
      }

      if (joinHandler && !joinHandler.isDestroyed?.()) {
        try {
          joinHandler.destroy?.();
        } catch {}
        setJoinHandler(null);
      }

      if (selectionHandler && !selectionHandler.isDestroyed?.()) {
        try {
          selectionHandler.destroy();
        } catch {}
        setSelectionHandler(null);
        selectedEntitiesRef.current = [];
      }

      if (editHandler && !editHandler.isDestroyed?.()) {
        try {
          editHandler.destroy();
        } catch {}
        setEditHandler(null);
      }

      if (viewerRef.current && !viewerRef.current.isDestroyed?.()) {
        try {
          cancelGeojsonEdit(viewerRef.current, controlPointEntities, featurePointMapping, originalCameraControls);
        } catch {}
      }

      applyUndoActionsBatch(viewerRef, currentLayerEditing, undoStackRef);
      setUndoStack([]);
      setRedoStack([]);

      setCurrentLayerEditing(null);

      if (currentLayerEditing?.arrow_visibility && viewerRef.current && !viewerRef.current.isDestroyed?.()) {
        toggleDirectionalArrows(viewerRef.current, currentLayerEditing.dataSource, true, directionArrowSettingRef);
      }
    } catch (err) {
      console.warn("cancelEditing failed:", err);
    }

    preEditGeojsonRef.current = null;
    preEditLayerIdRef.current = null;
  };

  const saveEditing = (opts = {}) => {
    const { layerOverride = null, keepCurrentLayer = false } = opts;
    const layerToSave = layerOverride || currentLayerEditing;

    setEditType("none");
    setEditMode(false);
    setCanHightlightEntity(true);
    restoreCameraControls(viewerRef.current, originalCameraControls);
    setCursorMode(Type.CURSOR.DEFAULT);
    cancelGeojsonEdit(viewerRef.current, controlPointEntities, featurePointMapping, originalCameraControls);
    cancelDrawing();

    // Release tool locks
    setActiveTool(TOOL.NONE);
    setIsOpenParallelDialog(false);
    setActiveArc(false);
    setActiveArc3(false);

    // Destroy handlers if they exist
    if (editHandler) {
      editHandler.destroy();
      setEditHandler(null);
    }
    const h = handlersRef.current?.split || splitHandler;
    if (h) {
      try {
        h.destroy?.();
      } catch {}
      setSplitHandler(null);
    }

    if (boxPromiseRef.current?.cancel) {
      boxPromiseRef.current.cancel();
      boxPromiseRef.current = null;
    }
    setActiveBox(false);
    setIsOpenBoxWidthDialog(false);
    setBoxCenterLine(null);
    setBoxPreviewLength(0);

    if (deleteHandler) {
      deleteHandler.destroy();
      setDeleteHandler(null);
    }
    if (reverseHandler) {
      reverseHandler.destroy();
      setReverseHandler(null);
    }

    // Update mapObjects with edited GeoJSON for the saved layer
    const updated = [...mapObjects];
    for (const key in updated) {
      if (layerToSave?.id != null && updated[key].id == layerToSave.id) {
        updated[key].geojson = getEditedGeoJSON(layerToSave);
        updated[key].last_geojson = preEditGeojsonRef.current;
        updated[key].is_edited = true;
      }
    }
    setMapObjects(updated);

    // Clear undo/redo stacks
    setUndoStack([]);
    setRedoStack([]);
    if (!keepCurrentLayer) setCurrentLayerEditing(null);

    preEditGeojsonRef.current = null;
    preEditLayerIdRef.current = null;
  };

  function applyUndoActionsBatch(viewerRef, currentLayerEditing, undoStackRef) {
    if (!viewerRef?.current) return;

    const stack = [...(undoStackRef.current || [])].reverse();

    const getEntitiesFrom = (dsRef) => dsRef?.entities || currentLayerEditing?.dataSource?.entities;

    // Helper: find an EntityCollection that contains an entity id
    const findEntitiesCollectionByEntityId = (entityId) => {
      const list = viewerRef.current?.dataSources?._dataSources || [];
      for (const ds of list) {
        if (ds?.entities?.getById(entityId)) return ds.entities;
      }
      return currentLayerEditing?.dataSource?.entities;
    };

    for (const action of stack) {
      switch (action.type) {
        case "delete": {
          const entities = getEntitiesFrom(action.dsRef) || currentLayerEditing.dataSource.entities;
          safeAddEntity(entities, action.entitySnapshot);
          break;
        }
        case "modify": {
          const entities = getEntitiesFrom(action.dsRef) || findEntitiesCollectionByEntityId(action.entityId);
          const existing = entities.getById(action.entityId);
          if (existing) entities.remove(existing);
          safeAddEntity(entities, action.entitySnapshot);
          break;
        }
        case "add": {
          const entities = getEntitiesFrom(action.dsRef) || findEntitiesCollectionByEntityId(action.entityId);
          if (typeof entities.removeById === "function") entities.removeById(action.entityId);
          else {
            const ex = entities.getById(action.entityId);
            if (ex) entities.remove(ex);
          }
          break;
        }

        case "split": {
          const entities = getEntitiesFrom(action.dsRef) || currentLayerEditing.dataSource.entities;
          if (Array.isArray(action.newEntities)) {
            for (const id of action.newEntities) {
              if (typeof entities.removeById === "function") entities.removeById(id);
              else {
                const e = entities.getById(id);
                if (e) entities.remove(e);
              }
            }
          }
          safeAddEntity(entities, action.originalEntity);
          break;
        }
        case "multi-split": {
          const entities = getEntitiesFrom(action.dsRef) || currentLayerEditing.dataSource.entities;
          for (const split of action.splits || []) {
            const { originalEntity, newEntities } = split;
            for (const id of newEntities || []) {
              if (typeof entities.removeById === "function") entities.removeById(id);
              else {
                const e = entities.getById(id);
                if (e) entities.remove(e);
              }
            }
            safeAddEntity(entities, originalEntity);
          }
          break;
        }
        case "bulk-modify": {
          // Reverse a group of modifications across possibly multiple DS
          for (const change of action.changes || []) {
            const { dsRef, entityId, entitySnapshot } = change;
            const entities = getEntitiesFrom(dsRef) || findEntitiesCollectionByEntityId(entityId);
            const existing = entities.getById(entityId);
            if (existing) entities.remove(existing);
            if (entitySnapshot) safeAddEntity(entities, entitySnapshot);
          }
          break;
        }
        default:
          console.warn("Unknown undo action type:", action.type);
      }
    }

    if (currentLayerEditing?.arrow_visibility) {
      toggleDirectionalArrows(viewerRef.current, currentLayerEditing.dataSource, true, directionArrowSettingRef);
    }
  }

  // For Split
  useEffect(() => {
    if (editType !== "cut") return;
    const handler = handleCuttingGeojsonLines(viewerRef.current, originalCameraControls, currentLayerEditing.dataSource, pushUndo, userData, currentLayerEditing, setMapObjects);
    setSplitHandler(handler);
    // Mark tool as active if not already
    if (activeTool === TOOL.NONE) setActiveTool(TOOL.SPLIT);

    return () => {
      try {
        handler.destroy?.();
      } catch {}
      setSplitHandler(null);
      // If we are turning off cut, release tool lock
      if (activeTool === TOOL.SPLIT) setActiveTool(TOOL.NONE);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editType]);

  // Auto-save previous layer when user switches layer while editMode is still true
  useEffect(() => {
    if (!editMode) {
      prevLayerEditingRef.current = currentLayerEditing;
      return;
    }

    const prev = prevLayerEditingRef.current;
    const next = currentLayerEditing;

    const prevKey = prev ? getLayerKey(prev) : "";
    const nextKey = next ? getLayerKey(next) : "";

    if (prev && next && prevKey && nextKey && prevKey !== nextKey) {
      setSavedPropsInNewEntity({});
      setSavedPropsInNewEntityLayerKey(nextKey);
      // Save previous layer edits, then re-init edit mode for the new layer
      saveEditing({
        layerOverride: prev,
        keepCurrentLayer: true,
      });

      // Re-init handlers for the new layer
      setTimeout(() => {
        setEditMode(true);
      }, 0);
    }

    prevLayerEditingRef.current = currentLayerEditing;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, currentLayerEditing]);

  // For reverse waypoints
  useEffect(() => {
    if (editType !== "reverse") return;

    const handler = enableReverseOnClick(
      viewerRef.current,
      () => {
        showToast("The selected entity’s path has been reversed", Type.NOTIFICATION.INFO);
      },
      undefined,
      true,
      directionArrowSettingRef,
      userData,
    );
    setReverseHandler(handler);
    // Mark tool as active if not already
    if (activeTool === TOOL.NONE) setActiveTool(TOOL.REVERSE);

    return () => {
      try {
        handler.destroy?.();
      } catch {}
      setReverseHandler(null);
      // Release lock if we were on reverse
      if (activeTool === TOOL.REVERSE) setActiveTool(TOOL.NONE);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editType]);

  function collectIdsFromGeoJSON(geojson, out = new Set()) {
    if (!geojson || !Array.isArray(geojson.features)) return out;

    for (const feature of geojson.features) {
      const n = Number(feature?.properties?.id);
      if (Number.isFinite(n)) out.add(n);
    }

    return out;
  }

  function getMaxIdFromGeoJSON(geojson) {
    const ids = collectIdsFromGeoJSON(geojson);
    let max = -Infinity;

    for (const id of ids) {
      if (id > max) max = id;
    }

    return max === -Infinity ? null : max;
  }

  function getMaxIdFromDataSource(ds) {
    if (!ds?.entities) return null;
    const now = JulianDate.now();
    let max = -Infinity;
    const list = ds.entities.values || [];

    for (const e of list) {
      const v = e.properties?.getValue?.(now) || {};
      const n = Number(v.id);
      if (Number.isFinite(n) && n > max) max = n;
    }

    return max === -Infinity ? null : max;
  }

  function collectIdsFromDataSource(ds, out = new Set()) {
    if (!ds?.entities) return out;
    const now = JulianDate.now();
    const list = ds.entities.values || [];

    for (const e of list) {
      const v = e.properties?.getValue?.(now) || {};
      const n = Number(v.id);
      if (Number.isFinite(n)) out.add(n);
    }

    return out;
  }

  function getStoredMaxIdFromLayer(layer) {
    const candidates = [layer?.bboxMaxFeaturePropertiesId, layer?.bboxMaxId];

    for (const candidate of candidates) {
      const value = Number(candidate);
      if (Number.isFinite(value)) return value;
    }

    return null;
  }

  function getFallbackMaxIdForLayer(layer) {
    let max = -Infinity;

    if (Number.isFinite(maxIdRef.current)) max = Math.max(max, maxIdRef.current);

    const lastId = Number(layer?.geojson_last_id);
    if (Number.isFinite(lastId)) max = Math.max(max, lastId);

    for (const gjson of [layer?.geojson, layer?.fullGeojson, layer?.hiddenGeojson, layer?.original_geojson]) {
      const gjsonMax = getMaxIdFromGeoJSON(gjson);
      if (Number.isFinite(gjsonMax)) max = Math.max(max, gjsonMax);
    }

    const dsMax = getMaxIdFromDataSource(layer?.dataSource);
    if (Number.isFinite(dsMax)) max = Math.max(max, dsMax);

    return Number.isFinite(max) ? max : 0;
  }

  function getLiveMaxId(layer) {
    const storedMax = getStoredMaxIdFromLayer(layer);
    if (Number.isFinite(storedMax)) return storedMax;

    return getFallbackMaxIdForLayer(layer);
  }

  function buildUsedFeatureIdSet(layer) {
    const ids = new Set();

    for (const gjson of [layer?.geojson, layer?.fullGeojson, layer?.hiddenGeojson, layer?.original_geojson]) {
      collectIdsFromGeoJSON(gjson, ids);
    }

    collectIdsFromDataSource(layer?.dataSource, ids);

    return ids;
  }

  function syncAllocatedIdToLayer(layer, nextId) {
    if (!layer || !Number.isFinite(nextId)) return;

    maxIdRef.current = nextId;
    layer.geojson_last_id = nextId;
    layer.bboxMaxId = nextId;
    layer.bboxMaxFeaturePropertiesId = nextId;

    setMapObjects((prev) =>
      prev.map((o) =>
        String(o?.id) === String(layer.id) || String(o?.layer_id) === String(layer.layer_id)
          ? {
              ...o,
              geojson_last_id: nextId,
              bboxMaxId: nextId,
              bboxMaxFeaturePropertiesId: nextId,
            }
          : o,
      ),
    );
  }

  function allocNextId() {
    const layer = currentLayerEditing;
    const usedIds = buildUsedFeatureIdSet(layer);
    let next = getLiveMaxId(layer) + 2;

    while (usedIds.has(next)) {
      next += 2;
    }

    syncAllocatedIdToLayer(layer, next);
    return next;
  }

  function isExactIdKey(key) {
    if (typeof key !== "string") return false;
    return key.toLowerCase() === "id";
  }

  const STYLE_PROPS = {
    point: ["pixelSize", "color", "outlineColor", "outlineWidth", "scaleByDistance", "translucencyByDistance", "distanceDisplayCondition", "heightReference"],
    polyline: ["material", "width", "clampToGround", "classificationType", "zIndex", "distanceDisplayCondition", "shadows", "depthFailMaterial", "arcType"],
    polygon: ["material", "outline", "outlineColor", "outlineWidth", "classificationType", "zIndex", "shadows", "stRotation", "distanceDisplayCondition"],
  };

  function copyProps(src, dst, keys) {
    if (!src || !dst) return;
    for (const k of keys) {
      if (src[k] !== undefined) dst[k] = src[k];
    }
  }
  function safeCssColor(css, fallback = Color.WHITE) {
    try {
      if (typeof css === "string" && css.trim()) return Color.fromCssColorString(css);
    } catch {}
    return fallback;
  }

  function normalizeTagColor(value) {
    if (!value) return null;
    const v = String(value).trim().toLowerCase();
    if (!v || v === "unknown" || v === "none" || v === "null") return null;
    return String(value).trim();
  }

  function normalizeSubtype(value) {
    if (!value) return null;
    const v = String(value).trim().toLowerCase();
    if (!["solid", "dashed", "dotted", "pattern"].includes(v)) return null;
    return v;
  }
  function applyTagOverridesOnly(targetEntity, layer) {
    if (!targetEntity) return;
    if (!isLaneMarkerLayer(layer)) return;

    const tags = targetEntity?.properties?.getValue?.(JulianDate.now()) || {};
    const tagColor = normalizeTagColor(tags.color);
    const subtype = normalizeSubtype(tags.subtype);

    const hasColor = !!tagColor;
    const hasSubtype = !!subtype;
    if (!hasColor && !hasSubtype) return;

    const c = hasColor ? safeCssColor(tagColor, Color.WHITE) : null;

    if (targetEntity.point && hasColor) {
      targetEntity.point.color = c;
      targetEntity.point.outlineColor = c;
    }

    if (targetEntity.polygon && hasColor) {
      targetEntity.polygon.material = c;
      targetEntity.polygon.outline = true;
      targetEntity.polygon.outlineColor = c;
    }

    if (targetEntity.polyline) {
      const lineColor = hasColor ? c : Color.YELLOW;

      if (hasSubtype) {
        if (subtype === "dashed") {
          targetEntity.polyline.material = new PolylineDashMaterialProperty({ color: lineColor, dashLength: 16 });
        } else if (subtype === "dotted") {
          targetEntity.polyline.material = new PolylineDashMaterialProperty({ color: lineColor, dashLength: 5 });
        } else if (subtype === "pattern") {
          targetEntity.polyline.material = new PolylineDashMaterialProperty({ color: lineColor, dashLength: 12, dashPattern: 255 });
        } else {
          targetEntity.polyline.material = new ColorMaterialProperty(lineColor);
        }
      } else if (hasColor) {
        targetEntity.polyline.material = new ColorMaterialProperty(lineColor);
      }
    }
  }

  function applyTemplateStyle(targetEntity, templateEntity, layer) {
    if (!targetEntity) return;

    if (templateEntity) {
      if (templateEntity.point && targetEntity.point) copyProps(templateEntity.point, targetEntity.point, STYLE_PROPS.point);
      if (templateEntity.polyline && targetEntity.polyline) copyProps(templateEntity.polyline, targetEntity.polyline, STYLE_PROPS.polyline);
      if (templateEntity.polygon && targetEntity.polygon) copyProps(templateEntity.polygon, targetEntity.polygon, STYLE_PROPS.polygon);
    }

    applyTagOverridesOnly(targetEntity, layer);
  }

  function getLayerKey(layer) {
    const v = layer?.id ?? layer?.layer_id ?? layer?.layerName ?? layer?.layer_name ?? layer?.name ?? "";
    const base = String(v || "").trim();
    if (base) return base;

    const file = String(layer?.file_name || "").trim();
    const name = String(layer?.name || layer?.layerName || layer?.layer_name || "").trim();
    const type = String(layer?.geojson_type || "").trim();
    return [file, name, type].filter(Boolean).join("|");
  }

  function isLaneMarkerLayer(layer) {
    const s = `${layer?.file_name || ""} ${layer?.name || ""} ${layer?.layerName || ""} ${layer?.layer_name || ""}`.toLowerCase();
    return /lane[\s_-]*marker/.test(s);
  }

  function sanitizePropsForLayer(layer, propsObj) {
    const out = { ...(propsObj || {}) };

    if (!isLaneMarkerLayer(layer)) {
      delete out.color;
      delete out.subtype;
      delete out["line-thin"];
      delete out.line_thin;
      delete out["line-style"];
      delete out.line_style;
      delete out.dashLength;
      delete out.dashPattern;
      delete out.defaultStyle;
      delete out.solidDashLength;
      delete out.dashedDashLength;
      delete out.dottedDashLength;
    }

    return out;
  }

  function getColumnKeys(layer) {
    return Array.isArray(layer?.geojson_table_columns) ? layer.geojson_table_columns.map((c) => c?.column_name).filter((v) => typeof v === "string" && v.length > 0) : [];
  }

  function filterPropsByColumns(layer, propsObj) {
    const cols = getColumnKeys(layer);
    if (!cols.length) return { ...(propsObj || {}) };

    const allowed = new Set(cols);
    const out = {};
    for (const [k, v] of Object.entries(propsObj || {})) {
      if (!k) continue;
      if (isExactIdKey(k)) continue;
      if (allowed.has(k)) out[k] = v;
    }
    return out;
  }

  function buildAutoPropsForNewFeature(layer, savedProps, savedLayerKey) {
    const feats = Array.isArray(layer?.geojson?.features) ? layer.geojson.features : [];
    const lastIndex = feats.length > 0 ? feats.length - 1 : -1;
    const lastFeatureProps = lastIndex >= 0 ? (feats[lastIndex]?.properties ?? {}) : {};

    const layerKey = getLayerKey(layer);
    const columnKeys = getColumnKeys(layer);

    const hasSaved = savedProps && typeof savedProps === "object" && Object.keys(savedProps).length > 0;
    const canUseSaved = hasSaved && savedLayerKey && layerKey && savedLayerKey === layerKey;

    const templateRaw = canUseSaved ? savedProps : lastFeatureProps;
    const templateFiltered = filterPropsByColumns(layer, templateRaw);

    const keys = [];
    keys.push("id");

    if (columnKeys.length > 0) {
      for (const k of columnKeys) {
        if (!k || isExactIdKey(k)) continue;
        keys.push(k);
      }
    } else {
      for (const k of Object.keys(templateFiltered)) {
        if (!k || isExactIdKey(k)) continue;
        keys.push(k);
      }
    }

    const nextId = allocNextId();

    const propsObj = {};
    for (const key of keys) {
      if (isExactIdKey(key)) propsObj[key] = nextId;
      else if (templateFiltered[key] !== undefined && templateFiltered[key] !== null) propsObj[key] = templateFiltered[key];
      else propsObj[key] = "";
    }

    propsObj["creator"] = getUsernameAndUserIdFormat(userData);

    return propsObj;
  }

  async function commitNewFeatureAuto(geojson, layer) {
    if (!layer?.dataSource || !geojson) return;

    const propsObj = buildAutoPropsForNewFeature(layer, savedPropsInNewEntity, savedPropsInNewEntityLayerKey);

    const feature = { ...geojson, properties: propsObj };

    const templateEntity = layer?.dataSource?.entities?.values?.[0] || null;

    const tempDataSource = await GeoJsonDataSource.load(feature);
    const entities = tempDataSource.entities.values || [];

    for (const entity of entities) {
      const newEntityOptions = {
        name: entity.name || "geojson feature",
        properties: entity.properties,
        geojsonType: Type.ASSET.GEOJSON,
      };

      if (entity.position?.getValue) {
        newEntityOptions.position = entity.position.getValue(JulianDate.now());
        newEntityOptions.point = { pixelSize: 13, color: Color.RED, outlineColor: Color.RED, outlineWidth: 1 };
      }
      if (entity.polyline) newEntityOptions.polyline = entity.polyline;
      if (entity.polygon) newEntityOptions.polygon = entity.polygon;

      const newEntity = layer.dataSource.entities.add(newEntityOptions);
      applyTemplateStyle(newEntity, templateEntity, layer);

      pushUndo({ type: "add", entitySnapshot: cloneEntity(newEntity), entityId: newEntity.id });
    }

    setMapObjects((prev) => prev.map((o) => (o.id === layer.id ? { ...o } : o)));

    const layerKey = getLayerKey(layer);
    setSavedPropsInNewEntityLayerKey(layerKey);

    setSavedPropsInNewEntity(() => {
      const filtered = sanitizePropsForLayer(layer, filterPropsByColumns(layer, propsObj));
      const next = {};

      for (const [k, v] of Object.entries(filtered)) {
        if (k && !isExactIdKey(k)) next[k] = v;
      }

      return next;
    });
  }

  const startQuadraticSplineOnce = useCallback(async () => {
    setActiveTool(TOOL.ARC);
    setActiveArc(true);

    const p = CreateEntityOnQuadraticBezier(viewerRef.current, currentLayerEditing.dataSource, { pushUndo: null });
    arcPromiseRef.current = p;

    try {
      const out = await p;
      const { geojson, autoSave } = out || {};

      if (autoSave) {
        if (geojson) await commitNewFeatureAuto(geojson, currentLayerEditing);
        arcPromiseRef.current = null;

        if (activeToolRef.current === TOOL.ARC && activeArcRef.current && !openPropertiesDialog) {
          setTimeout(() => {
            startQuadraticSplineOnceRef.current?.();
          }, 0);
        }
        return;
      }

      setNewGeojson(geojson);
      setOpenPropertiesDialog(true);
      setEditType("none");

      setActiveArc(false);
      setActiveTool(TOOL.NONE);
      setCursorMode(Type.CURSOR.DEFAULT);
    } catch (e) {
      setActiveArc(false);
      setActiveTool(TOOL.NONE);
      setCursorMode(Type.CURSOR.DEFAULT);
    } finally {
      arcPromiseRef.current = null;
    }
  }, [TOOL, currentLayerEditing, openPropertiesDialog]);

  const startCubicSplineOnce = useCallback(async () => {
    setActiveTool(TOOL.ARC_CUBIC);
    setActiveArc3(true);

    const p = CreateEntityOnCubicBezier(viewerRef.current, currentLayerEditing.dataSource, {
      pushUndo: null,
      snappingOptions,
      sampleSpacing: lineSampleRate || 1,
      previewSampleSpacing: lineSampleRate || 1,
    });
    arcPromiseRef.current = p;

    try {
      const out = await p;
      const { geojson, autoSave } = out || {};

      if (autoSave) {
        if (geojson) await commitNewFeatureAuto(geojson, currentLayerEditing);
        arcPromiseRef.current = null;

        if (activeToolRef.current === TOOL.ARC_CUBIC && activeArc3Ref.current && !openPropertiesDialog) {
          setTimeout(() => {
            startCubicSplineOnceRef.current?.();
          }, 0);
        }
        return;
      }

      setNewGeojson(geojson);
      setOpenPropertiesDialog(true);
      setEditType("none");

      setActiveArc3(false);
      setActiveTool(TOOL.NONE);
      setCursorMode(Type.CURSOR.DEFAULT);
    } catch (e) {
      setActiveArc3(false);
      setActiveTool(TOOL.NONE);
      setCursorMode(Type.CURSOR.DEFAULT);
    } finally {
      arcPromiseRef.current = null;
    }
  }, [TOOL, currentLayerEditing, snappingOptions, lineSampleRate, openPropertiesDialog]);

  const drawNewEntity = async () => {
    // Guard: prevent switching while another tool is active
    if (!canStartTool(TOOL.NEW)) return;

    const toggled = editType === "new" ? "none" : "new";
    setEditType(toggled);
    setCursorMode(toggled === "new" ? Type.CURSOR.PLUS : Type.CURSOR.DEFAULT);

    if (toggled === "new") {
      setActiveTool(TOOL.NEW); // lock
      await handleDrawingNewEntity();
    } else {
      // If user toggles off "new", release lock
      setActiveTool(TOOL.NONE);
      cancelDrawing();
    }
  };

  const handleDrawingNewEntity = async () => {
    let type = getGeometryTypeGeojson(currentLayerEditing.geojson);
    if (!type) {
      if (currentLayerEditing?.geojson_geometry_type) {
        type = normalizeGeometryType(currentLayerEditing?.geojson_geometry_type);
      } else {
        type = newFeatureType;
      }
    }

    const { handler, promise, finishDrawing } = drawingNewEntity(viewerRef.current, type, lineSampleRateRef, snappingOptions, { autoJumpEnabledRef });

    drawHandlerRef.current = handler;
    drawSessionRef.current = { handler, promise, finishDrawing };

    try {
      const { geojson } = await promise;

      if (autoSaveNewRef.current) {
        autoSaveNewRef.current = false;
        await commitNewFeatureAuto(geojson, currentLayerEditing);
        restartNewRef.current = true;
        return;
      }

      setNewGeojson(geojson);
      setOpenPropertiesDialog(true);
      setEditType("none");
    } catch (err) {
      setEditType("none");
      setCursorMode(Type.CURSOR.DEFAULT);
      setNewGeojson(null);
      setOpenPropertiesDialog(false);
      setActiveTool(TOOL.NONE);
    } finally {
      drawHandlerRef.current = null;
      drawSessionRef.current = null;

      if (restartNewRef.current) {
        restartNewRef.current = false;

        // keep tool active, just start a fresh draw session
        if (activeToolRef.current === TOOL.NEW && editTypeRef.current === "new" && !openPropertiesDialog) {
          setTimeout(() => {
            handleDrawingNewEntity();
          }, 0);
        }
      }
    }
  };

  const cancelDrawing = () => {
    if (drawHandlerRef.current) {
      drawSessionRef.current.finishDrawing();
    }
    setEditType("none");
    setNewGeojson(null);
    setOpenPropertiesDialog(false);

    if (activeTool === TOOL.NEW) setActiveTool(TOOL.NONE);
  };

  useEffect(() => {
    if (activeTool === TOOL.NEW && !openPropertiesDialog && editType === "none" && !drawHandlerRef.current) {
      setActiveTool(TOOL.NONE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPropertiesDialog]);

  const cutFunction = () => {
    if (!canStartTool(TOOL.SPLIT)) return;
    const toggled = editType === "cut" ? "none" : "cut";
    setEditType(toggled);
    setCursorMode(toggled === "cut" ? Type.CURSOR.CUT : Type.CURSOR.DEFAULT);
    if (toggled === "cut") setActiveTool(TOOL.SPLIT);
    else if (activeTool === TOOL.SPLIT) setActiveTool(TOOL.NONE);
  };

  const reverseFunction = () => {
    if (!canStartTool(TOOL.REVERSE)) return;
    const toggled = editType === "reverse" ? "none" : "reverse";
    setEditType(toggled);
    if (toggled === "reverse") setActiveTool(TOOL.REVERSE);
    else if (activeTool === TOOL.REVERSE) setActiveTool(TOOL.NONE);
  };

  const undoFunction = () => {
    const action = popUndo();
    if (!action) return;

    // Helper: get the right EntityCollection from stored dsRef or fallbacks
    const getEntitiesFrom = (dsRef) => dsRef?.entities || currentLayerEditing?.dataSource?.entities;

    // Helper: search an entity by id across all dataSources if dsRef is missing
    const findEntitiesCollectionByEntityId = (entityId) => {
      const list = viewerRef.current?.dataSources?._dataSources || [];
      for (const ds of list) {
        if (ds?.entities?.getById(entityId)) return ds.entities;
      }
      return currentLayerEditing?.dataSource?.entities;
    };

    switch (action.type) {
      case "delete": {
        const entities = getEntitiesFrom(action.dsRef) || currentLayerEditing.dataSource.entities;
        // Re-add the deleted snapshot
        safeAddEntity(entities, action.entitySnapshot);
        // Prepare redo
        const existing = entities.getById(action.entitySnapshot.id);
        if (existing) pushRedo({ type: "delete", entitySnapshot: cloneEntity(existing), entityId: existing.id, dsRef: action.dsRef });
        break;
      }

      case "modify": {
        const entities = getEntitiesFrom(action.dsRef) || findEntitiesCollectionByEntityId(action.entityId);
        const existing = entities.getById(action.entityId);
        if (existing) {
          const currentSnapshot = cloneEntity(existing);
          // Save current state for redo and remove the current entity
          pushRedo({ type: "modify", entitySnapshot: currentSnapshot, entityId: action.entityId, dsRef: action.dsRef });
          entities.remove(existing);
        }
        // Restore original snapshot
        safeAddEntity(entities, action.entitySnapshot);
        break;
      }

      case "add": {
        const entities = getEntitiesFrom(action.dsRef) || findEntitiesCollectionByEntityId(action.entityId);
        const existing = entities.getById(action.entityId);
        if (existing) {
          // If entity exists, create redo to re-add it later and remove now
          pushRedo({ type: "add", entitySnapshot: cloneEntity(existing), entityId: action.entityId, dsRef: action.dsRef });
          entities.remove(existing);
        } else {
          // Nothing existed - redo should re-add the snapshot
          pushRedo({ type: "add", entitySnapshot: action.entitySnapshot, entityId: action.entityId, dsRef: action.dsRef });
        }
        break;
      }
      case "add-many": {
        const entities = getEntitiesFrom(action.dsRef) || findEntitiesCollectionByEntityId(action.entityIds?.[0]);
        const removedSnapshots = [];

        for (const id of action.entityIds || []) {
          const e = entities?.getById?.(id);
          if (e) {
            removedSnapshots.push(cloneEntity(e));
            entities.remove(e);
          }
        }

        pushRedo({
          type: "add-many",
          entityIds: (removedSnapshots || []).map((s) => s.id),
          entitySnapshots: removedSnapshots,
          dsRef: action.dsRef,
        });

        break;
      }

      case "split": {
        const entities = getEntitiesFrom(action.dsRef) || currentLayerEditing.dataSource.entities;
        const newEntitiesSnapshots = [];
        if (Array.isArray(action.newEntities)) {
          for (const id of action.newEntities) {
            const e = entities.getById(id);
            if (e) {
              newEntitiesSnapshots.push(cloneEntity(e));
              entities.remove(e);
            }
          }
        }
        // Re-add original entity and push redo info for the split operation
        safeAddEntity(entities, action.originalEntity);

        pushRedo({ type: "split", originalEntity: cloneEntity(action.originalEntity), newEntities: newEntitiesSnapshots, dsRef: action.dsRef });
        break;
      }

      case "multi-split": {
        const redoSplits = [];
        const entities = getEntitiesFrom(action.dsRef) || currentLayerEditing.dataSource.entities;

        for (const split of action.splits || []) {
          const { originalEntity, newEntities } = split;
          const newEntitiesSnapshots = [];
          for (const id of newEntities || []) {
            const e = entities.getById(id);
            if (e) {
              newEntitiesSnapshots.push(cloneEntity(e));
              entities.remove(e);
            }
          }
          safeAddEntity(entities, originalEntity);

          redoSplits.push({ originalEntity: cloneEntity(originalEntity), newEntities: newEntitiesSnapshots });
        }

        pushRedo({ type: "multi-split", splits: redoSplits, dsRef: action.dsRef });
        break;
      }

      case "bulk-modify": {
        // action.changes: Array<{ dsRef, entityId, entitySnapshot }>
        const redoChanges = [];

        for (const change of action.changes || []) {
          const { dsRef, entityId, entitySnapshot } = change;
          const entities = getEntitiesFrom(dsRef) || findEntitiesCollectionByEntityId(entityId);
          const existing = entities.getById(entityId);
          if (existing) {
            // capture current (post-change) state for redo and remove current
            redoChanges.push({ dsRef, entityId, entitySnapshot: cloneEntity(existing) });
            entities.remove(existing);
          } else {
            // If entity is missing, redo should try to remove what we're about to add
            redoChanges.push({ dsRef, entityId, entitySnapshot: null });
          }
          safeAddEntity(entities, entitySnapshot);
        }

        pushRedo({ type: "bulk-modify", changes: redoChanges });
        break;
      }

      default:
        console.warn("Unknown undo action type:", action.type);
    }

    if (currentLayerEditing?.arrow_visibility) {
      toggleDirectionalArrows(viewerRef.current, currentLayerEditing.dataSource, true, directionArrowSettingRef);
    }
  };

  const safeAddEntity = (entities, snapshot) => {
    if (!entities || !snapshot) return;
    const existing = snapshot.id ? entities.getById(snapshot.id) : null;
    if (existing) entities.remove(existing);
    entities.add(snapshot);
  };

  const redoFunction = () => {
    if (!currentLayerEditing || !currentLayerEditing.dataSource) return;

    const redoAction = popRedo();
    if (!redoAction) return;

    const entities = currentLayerEditing.dataSource.entities;

    switch (redoAction.type) {
      case "add":
        entities.add(redoAction.entitySnapshot);
        pushUndo(redoAction);
        break;
      case "add-many": {
        const addedIds = [];

        for (const snap of redoAction.entitySnapshots || []) {
          const cloned = cloneEntity(snap);
          entities.add(cloned);
          addedIds.push(cloned.id);
        }

        pushUndo({
          type: "add-many",
          entityIds: addedIds,
          entitySnapshots: (redoAction.entitySnapshots || []).map((s) => cloneEntity(s)),
          dsRef: redoAction.dsRef,
        });

        break;
      }

      case "delete": {
        const toDelete = entities.getById(redoAction.entityId);
        if (toDelete) entities.remove(toDelete);
        pushUndo(redoAction);
        break;
      }

      case "modify": {
        const entityToModify = entities.getById(redoAction.entityId);
        if (entityToModify) {
          const currentSnapshot = cloneEntity(entityToModify);
          const newSnapshot = redoAction.entitySnapshot;

          // Apply geometry updates depending on entity type (polyline/polygon)
          if (entityToModify.polyline && newSnapshot.polyline?.positions) {
            entityToModify.polyline.positions = newSnapshot.polyline.positions;
          } else if (entityToModify.polygon && newSnapshot.polygon?.hierarchy) {
            entityToModify.polygon.hierarchy = newSnapshot.polygon.hierarchy;
          }

          // Material/width updates
          if (newSnapshot.polyline?.material) {
            entityToModify.polyline.material = newSnapshot.polyline.material;
          }
          if (newSnapshot.polyline?.width) {
            entityToModify.polyline.width = newSnapshot.polyline.width;
          }
          if (newSnapshot.polygon?.material) {
            entityToModify.polygon.material = newSnapshot.polygon.material;
          }

          // push undo for redo operation
          pushUndo({
            type: "modify",
            entitySnapshot: currentSnapshot,
            entityId: redoAction.entityId,
          });
        }
        break;
      }

      case "split": {
        const existing = entities.getById(redoAction.originalEntity.id);
        if (existing) {
          entities.remove(existing);
        }

        const newEntities = [];

        if (Array.isArray(redoAction.newEntities)) {
          for (const e of redoAction.newEntities) {
            const cloned = cloneEntity(e);
            entities.add(cloned);
            newEntities.push(cloned.id);
          }
        }

        pushUndo({
          type: "split",
          originalEntity: cloneEntity(redoAction.originalEntity),
          newEntities: newEntities,
        });
        break;
      }

      case "multi-split": {
        const undoSplits = [];

        for (const split of redoAction.splits) {
          const { originalEntity, newEntities } = split;

          const existing = entities.getById(originalEntity.id);
          if (existing) {
            entities.remove(existing);
          }

          const newEntityClones = [];

          for (const e of newEntities) {
            const cloned = cloneEntity(e);
            entities.add(cloned);
            newEntityClones.push(cloned.id);
          }

          undoSplits.push({
            originalEntity: cloneEntity(originalEntity),
            newEntities: newEntityClones,
          });
        }

        pushUndo({
          type: "multi-split",
          splits: undoSplits,
        });
        break;
      }

      default:
        console.warn("Unknown redo action type:", redoAction.type);
    }

    // Force a shallow update to mapObjects and restore arrows if needed
    setMapObjects((prev) => [...prev]);

    if (currentLayerEditing.arrow_visibility) {
      toggleDirectionalArrows(viewerRef.current, currentLayerEditing.dataSource, true, directionArrowSettingRef);
    }
  };

  const idGenerator = useCallback(() => {
    let base = 0;
    try {
      base = (currentLayerEditing?.dataSource?.entities?.values || []).length;
    } catch (_) {}

    if (entityIdRef.current == null) entityIdRef.current = base + 1;
    else entityIdRef.current += 1;

    return `ent_${entityIdRef.current}`;
  }, [currentLayerEditing?.dataSource]);

  const copyPaste = useMemo(() => {
    if (!viewerRef?.current || !currentLayerEditing.dataSource) return null;
    return createCopyPasteManager({
      viewer: viewerRef.current,
      dataSource: currentLayerEditing.dataSource,
      selectedEntitiesRef,
      pushUndo, // optional
      idGenerator, // optional
      defaultOffsetMeters: { east: 10, north: 10, up: 0 }, // fallback when depth-pick fails
    });
  }, [viewerRef?.current, currentLayerEditing.dataSource, selectedEntitiesRef, pushUndo, idGenerator]);
  useEffect(() => {
    if (!copyPaste) return;
    const onKey = (e) => copyPaste.onKeyDown(e, editMode);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      copyPaste.destroy();
    };
  }, [copyPaste, editMode]);

  const arcPromiseRef = useRef(null);
  const parallelPromiseRef = useRef(null);

  const startQuadraticSplineOnceRef = useRef(null);
  const startCubicSplineOnceRef = useRef(null);

  useEffect(() => {
    startQuadraticSplineOnceRef.current = startQuadraticSplineOnce;
    startCubicSplineOnceRef.current = startCubicSplineOnce;
  }, [startQuadraticSplineOnce, startCubicSplineOnce]);

  const isPolygonLayer = useMemo(() => {
    if (!currentLayerEditing) return false;

    let t = getGeometryTypeGeojson(currentLayerEditing.geojson);
    if (!t && currentLayerEditing?.geojson_geometry_type) t = normalizeGeometryType(currentLayerEditing.geojson_geometry_type);

    return (t || "").toLowerCase() === "polygon";
  }, [currentLayerEditing]);

  const isPolylineLayer = useMemo(() => {
    if (!currentLayerEditing) return false;

    let t = getGeometryTypeGeojson(currentLayerEditing.geojson);
    if (!t && currentLayerEditing?.geojson_geometry_type) {
      t = normalizeGeometryType(currentLayerEditing.geojson_geometry_type);
    }

    const tt = (t || "").toLowerCase();
    return tt === "linestring" || tt === "multilinestring";
  }, [currentLayerEditing]);

  const finalizeDigitizer = useCallback(
    (out) => {
      digitizerCtlRef.current = null;

      setActiveTool(TOOL.NONE);
      setCursorMode(Type.CURSOR.DEFAULT);

      if (out?.geojson) {
        setNewGeojson(out.geojson);
        setOpenPropertiesDialog(true);
        setEditType("none");
      }
    },
    [TOOL],
  );

  const startDigitizerAssistantInternal = useCallback(() => {
    const viewer = viewerRef.current;
    const ds = currentLayerEditing?.dataSource;

    if (!viewer || !ds) {
      showToast("No active layer/viewer for digitizer.", Type.NOTIFICATION.WARNING);
      return;
    }

    if (digitizerCtlRef.current?.cancel) digitizerCtlRef.current.cancel();

    digitizerCtlRef.current = createDigitizerAssistant({
      viewer,
      dataSource: ds,
      getMinDistance: () => digitizerMinDistanceRef.current,
      getMaxDistance: () => digitizerMaxDistanceRef.current,
      getDuration: () => digitizerDurationRef.current,
      onStop: (out) => finalizeDigitizer(out),
    });

    setActiveTool(TOOL.DIGITIZER_ASSISTANT);
  }, [TOOL, currentLayerEditing, finalizeDigitizer, viewerRef]);

  const startDigitizer = useCallback(() => {
    if (!canStartTool(TOOL.DIGITIZER_ASSISTANT)) return;
    setIsOpenDigitizerDialog(false);
    startDigitizerAssistantInternal();
  }, [TOOL, canStartTool, startDigitizerAssistantInternal]);

  const stopDigitizer = useCallback(() => {
    const out = digitizerCtlRef.current?.stop?.();
    finalizeDigitizer(out);
  }, [finalizeDigitizer]);

  useEffect(() => {
    const isMod = (k) => ["shift", "ctrl", "alt", "meta", "space"].includes(String(k).toLowerCase());
    const isAlpha = (k) => /^[a-z]$/i.test(String(k || ""));
    const normalizeKey = (k) => {
      let key = String(k).toLowerCase();
      if (key === "control") key = "ctrl";
      if (key === " ") key = "space";
      return key;
    };

    const getCombo = (type) => savedHotkeysRef.current.find((h) => h.type === type)?.combination || [];

    const pressedKeys = new Set();

    const matches = (e, combo) => {
      if (!combo || combo.length === 0) return false;

      const norm = combo.map(normalizeKey);

      const want = {
        shift: norm.includes("shift"),
        ctrl: norm.includes("ctrl"),
        alt: norm.includes("alt"),
        meta: norm.includes("meta"),
        space: norm.includes("space"),
      };

      if (e.shiftKey !== want.shift) return false;
      if (e.ctrlKey !== want.ctrl) return false;
      if (e.altKey !== want.alt) return false;
      if (e.metaKey !== want.meta) return false;

      if (want.space && !pressedKeys.has("space")) return false;

      const main = norm.find((k) => !isMod(k));
      if (!main) return true;

      const pressed = normalizeKey(e.key);
      return pressed === main;
    };

    // -------------------------
    // 3-key sequential support
    // combo: [mod, letter1, letter2] (+ optional space as held modifier)
    // user: mod+letter1, then within 600ms press letter2 (with same mods or no mods; space allowed if required)
    // -------------------------
    const PENDING_KEY = "__editing_hotkey_pending__";
    if (!window[PENDING_KEY]) {
      window[PENDING_KEY] = {
        active: false,
        type: null,
        mods: [],
        wantSpace: false,
        stepKey: "",
        finalKey: "",
        expiresAt: 0,
        tid: null,
      };
    }
    const pending = window[PENDING_KEY];

    const resetPending = () => {
      if (pending.tid) clearTimeout(pending.tid);
      pending.active = false;
      pending.type = null;
      pending.mods = [];
      pending.wantSpace = false;
      pending.stepKey = "";
      pending.finalKey = "";
      pending.expiresAt = 0;
      pending.tid = null;
    };

    const modsMatchExactly = (e, mods) => {
      const needShift = mods.includes("shift");
      const needCtrl = mods.includes("ctrl");
      const needAlt = mods.includes("alt");
      const needMeta = mods.includes("meta");

      if (e.shiftKey !== needShift) return false;
      if (e.ctrlKey !== needCtrl) return false;
      if (e.altKey !== needAlt) return false;
      if (e.metaKey !== needMeta) return false;
      return true;
    };

    const modsAreNone = (e) => !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey;

    const isSequential3Combo = (combo) => {
      if (!combo || combo.length !== 3) return false;

      const norm = combo.map(normalizeKey);
      const mods = norm.filter((k) => isMod(k) && k !== "space");
      const nonMods = norm.filter((k) => !isMod(k));

      return mods.length === 1 && nonMods.length === 2 && isAlpha(nonMods[0]) && isAlpha(nonMods[1]);
    };

    const getSequentialParts = (combo) => {
      const norm = combo.map(normalizeKey);
      const wantSpace = norm.includes("space");
      const mods = norm.filter((k) => isMod(k) && k !== "space");
      const nonMods = norm.filter((k) => !isMod(k));
      return { mods, wantSpace, stepKey: nonMods[0], finalKey: nonMods[1] };
    };

    const startPending = (type, mods, wantSpace, stepKey, finalKey) => {
      resetPending();
      pending.active = true;
      pending.type = type;
      pending.mods = mods;
      pending.wantSpace = wantSpace;
      pending.stepKey = stepKey;
      pending.finalKey = finalKey;
      pending.expiresAt = Date.now() + 3000;
      pending.tid = setTimeout(() => resetPending(), 3000);
    };

    const shouldIgnore = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      return tag === "input" || tag === "textarea" || e.target?.isContentEditable;
    };

    const runAction = (type, e) => {
      const check = (t, selector, fn) => {
        if (t !== type) return false;
        e.preventDefault();
        e.stopPropagation();
        if (fn) fn();
        else document.querySelector(`[data-tool="${selector}"]`)?.click();
        return true;
      };

      if (check(Type.HOTKEYS.SAVE_EDITING, "save-btn")) return true;
      if (check(Type.HOTKEYS.DELETE_SELECTED, "delete-btn")) return true;
      if (check(Type.HOTKEYS.REVERSE_WAYPOINTS, "reverse-btn")) return true;
      if (check(Type.HOTKEYS.SPLITEDIT, "split-btn")) return true;
      if (check(Type.HOTKEYS.ADD_NEW_FEATURE, "new-btn")) return true;
      if (check(Type.HOTKEYS.UNDOEDIT, "undo-btn")) return true;
      if (check(Type.HOTKEYS.REDO, "redo-btn")) return true;
      if (check(Type.HOTKEYS.JOIN_LINES, "join-btn")) return true;
      if (check(Type.HOTKEYS.SPLINE, "spline-btn")) return true;
      if (check(Type.HOTKEYS.PARALLEL, "parallel-btn")) return true;
      if (check(Type.HOTKEYS.CANCEL_EDITING, "cancel-btn")) return true;

      return false;
    };

    const handleKeyDown = async (e) => {
      pressedKeys.add(normalizeKey(e.key));

      if (!editMode || !currentLayerEditing) return;
      if (shouldIgnore(e)) return;

      const key = normalizeKey(e.key);

      const isPlus = !e.ctrlKey && !e.metaKey && !e.altKey && (e.code === "Equal" || e.code === "NumpadAdd" || key === "+" || key === "=");

      if (isPlus && activeToolRef.current === TOOL.NEW && drawSessionRef.current?.finishDrawing && !openPropertiesDialog) {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) {
          autoJumpEnabledRef.current = !autoJumpEnabledRef.current; // Shift + + => toggle
        } else {
          autoJumpEnabledRef.current = true;
        }

        return;
      }

      if (key === "space") {
        e.preventDefault();
        e.stopPropagation();
      }

      // --- pending final key check ---
      if (pending.active) {
        if (Date.now() > pending.expiresAt) {
          resetPending();
        } else {
          const okMods = modsMatchExactly(e, pending.mods) || modsAreNone(e);
          const okSpace = !pending.wantSpace || pressedKeys.has("space");

          if (okMods && okSpace && key === normalizeKey(pending.finalKey)) {
            e.preventDefault();
            e.stopPropagation();
            const type = pending.type;
            resetPending();
            runAction(type, e);
            return;
          }

          if (!isMod(key)) resetPending();
        }
      }

      if (key === "h") {
        e.preventDefault();
        e.stopPropagation();

        const layerId = currentLayerEditing?.id;
        const list = mapObjectsRef?.current || mapObjects || [];

        const itemFromState = list.find((o) => String(o?.id) === String(layerId)) || list.find((o) => String(o?.layer_id) === String(layerId)) || null;

        await toggleAssetVisibility(itemFromState || currentLayerEditing);
        return;
      }

      // --- keep your special "s" logic untouched ---
      if (key === "s" && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey) {
        if (activeTool === TOOL.NEW && drawSessionRef.current?.finishDrawing && !openPropertiesDialog) {
          autoSaveNewRef.current = true;
          e.preventDefault();
          e.stopPropagation();
          drawSessionRef.current.finishDrawing();
          return;
        }

        if (activeTool === TOOL.DIGITIZER_ASSISTANT && digitizerCtlRef.current?.stop && !openPropertiesDialog) {
          e.preventDefault();
          e.stopPropagation();

          const out = digitizerCtlRef.current.stop();
          if (out?.geojson) await commitNewFeatureAuto(out.geojson, currentLayerEditing);

          startDigitizerAssistantInternal();
          return;
        }
      }

      // --- ordered hotkeys (same order as your original checks) ---
      const ordered = [
        Type.HOTKEYS.SAVE_EDITING,
        Type.HOTKEYS.DELETE_SELECTED,
        Type.HOTKEYS.REVERSE_WAYPOINTS,
        Type.HOTKEYS.SPLITEDIT,
        Type.HOTKEYS.ADD_NEW_FEATURE,
        Type.HOTKEYS.UNDOEDIT,
        Type.HOTKEYS.REDO,
        Type.HOTKEYS.JOIN_LINES,
        Type.HOTKEYS.SPLINE,
        Type.HOTKEYS.PARALLEL,
        Type.HOTKEYS.CANCEL_EDITING,
      ];

      for (const type of ordered) {
        const combo = getCombo(type);

        // sequential 3-key support
        if (isSequential3Combo(combo)) {
          const { mods, wantSpace, stepKey, finalKey } = getSequentialParts(combo);

          const okMods = modsMatchExactly(e, mods);
          const okSpace = !wantSpace || pressedKeys.has("space");

          if (okMods && okSpace && key === normalizeKey(stepKey)) {
            e.preventDefault();
            e.stopPropagation();
            startPending(type, mods, wantSpace, stepKey, finalKey);
            return;
          }
        }

        // normal one-shot support
        if (matches(e, combo)) {
          resetPending();
          if (runAction(type, e)) return;
        }
      }
    };

    const handleKeyUp = (e) => {
      pressedKeys.delete(normalizeKey(e.key));
    };

    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("keyup", handleKeyUp, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("keyup", handleKeyUp, true);
      resetPending();
    };
  }, [editMode, currentLayerEditing, savedHotkeysRef, activeTool, openPropertiesDialog, startDigitizerAssistantInternal]);

  useEffect(() => {
    return () => {
      if (digitizerCtlRef.current?.cancel) digitizerCtlRef.current.cancel();
      digitizerCtlRef.current = null;
    };
  }, []);

  return (
    <Portal>
      <Box position="fixed" bottom="5px" left="50%" transform="translateX(-50%)" zIndex={1005}>
        <Text bg="white" px={3} py={1} mb={1} borderRadius="md" boxShadow="lg" width={"fit-content"}>
          Layer: {removeFileExtension(currentLayerEditing.file_name)}
        </Text>

        <VStack alignItems={"start"}>
          <ButtonGroup spacing={4} bg="white" px={3} py={2} borderRadius="md" boxShadow="lg">
            <Tooltip content={"Save Changes " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.SAVE_EDITING)}>
              <Button data-tool="save-btn" width="45px" height="45px" colorPalette="green" variant="outline" mr={3} onClick={saveEditing}>
                <LuSaveAll />
              </Button>
            </Tooltip>

            <Tooltip content={"Delete Features " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.DELETE_SELECTED)}>
              <Button
                data-tool="delete-btn"
                disabled={selectedEntitiesRef.current.length === 0}
                width="45px"
                height="45px"
                colorPalette="red"
                variant={editType === "delete" ? "solid" : "outline"}
                onClick={() => {
                  for (const key in selectedEntitiesRef.current) {
                    resetEntityStyle(selectedEntitiesRef.current[key]);
                  }
                  deleteSelectedEntities(selectedEntitiesRef, pushUndo);
                }}
              >
                <LuTrash />
              </Button>
            </Tooltip>

            {/* REVERSE (single-tool guarded) */}
            <Tooltip content={"Reverse Waypoints " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.REVERSE_WAYPOINTS)}>
              <Button
                data-tool="reverse-btn"
                width="45px"
                height="45px"
                colorPalette="gray"
                variant={activeTool === TOOL.REVERSE || editType === "reverse" ? "solid" : "outline"}
                disabled={lockedTo(TOOL.REVERSE)}
                onClick={reverseFunction}
              >
                <LuSendToBack />
              </Button>
            </Tooltip>

            {/* SPLIT (single-tool guarded) */}
            <Tooltip content={"Split Features " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.SPLITEDIT)}>
              <Button
                data-tool="split-btn"
                width="45px"
                height="45px"
                colorPalette="gray"
                variant={activeTool === TOOL.SPLIT || editType === "cut" ? "solid" : "outline"}
                disabled={lockedTo(TOOL.SPLIT)}
                onClick={cutFunction}
              >
                <LuSquareSplitHorizontal />
              </Button>
            </Tooltip>

            {/* JOIN LINES (one-shot) */}
            <Tooltip content={"Join Lines " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.JOIN_LINES)}>
              <Button
                data-tool="join-btn"
                width="45px"
                height="45px"
                colorPalette="gray"
                variant="outline" // not a toggle
                disabled={activeTool !== TOOL.NONE}
                onClick={() => {
                  // call the one-shot join; if success, refresh shallow state if needed
                  const ok = joinTwoSelectedPolylinesOnce({
                    selectedEntitiesRef,
                    pushUndo,
                    userData,
                  });
                  if (ok) {
                    // optional: force a shallow refresh so UI reflects geometry change
                    setMapObjects((prev) => [...prev]);
                  }
                }}
              >
                <LuMerge />
              </Button>
            </Tooltip>

            {currentLayerEditing?.dataSource?.entities?.values?.length > 0 ? (
              <Tooltip content={"Add New Feature " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.ADD_NEW_FEATURE) + "\nYou can toggle auto-move with Shift + Plus"}>
                <Button
                  data-tool="new-btn"
                  width="45px"
                  height="45px"
                  colorPalette="gray"
                  variant={activeTool === TOOL.NEW || editType === "new" ? "solid" : "outline"}
                  disabled={lockedTo(TOOL.NEW)}
                  onClick={drawNewEntity}
                >
                  <LuPlus />
                </Button>
              </Tooltip>
            ) : (
              <Popover.Root open={isOpenFeatureTypeDialog} onOpenChange={(e) => setIsOpenFeatureTypeDialog(e.open)}>
                <Popover.Trigger asChild>
                  <Button
                    data-tool="new-btn"
                    width="45px"
                    height="45px"
                    colorPalette="gray"
                    variant={activeTool === TOOL.PARALLEL ? "solid" : "outline"}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Spacebar" || e.key === "Space") {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  >
                    <Tooltip content="Add New Feature">
                      <LuPlus />
                    </Tooltip>
                  </Button>
                </Popover.Trigger>
                <Portal>
                  <Popover.Positioner>
                    <Popover.Content>
                      <Popover.Arrow />
                      <Popover.Body>
                        <VStack gap={1}>
                          <Heading width={"full"} mb={2}>
                            New Feature
                          </Heading>
                          <Text width={"full"} fontSize={"16px"} mb={2}>
                            Type
                          </Text>
                          <HStack gap={3} width={"full"} mb={2}>
                            <Checkbox.Root checked={newFeatureType === "Point"} onCheckedChange={() => setNewFeatureType("Point")}>
                              <Checkbox.HiddenInput />
                              <Checkbox.Control />
                              <Checkbox.Label>Point</Checkbox.Label>
                            </Checkbox.Root>
                            <Checkbox.Root checked={newFeatureType === "LineString"} onCheckedChange={() => setNewFeatureType("LineString")}>
                              <Checkbox.HiddenInput />
                              <Checkbox.Control />
                              <Checkbox.Label>LineString</Checkbox.Label>
                            </Checkbox.Root>
                            <Checkbox.Root checked={newFeatureType === "Polygon"} onCheckedChange={() => setNewFeatureType("Polygon")}>
                              <Checkbox.HiddenInput />
                              <Checkbox.Control />
                              <Checkbox.Label>Polygon</Checkbox.Label>
                            </Checkbox.Root>
                          </HStack>

                          <Button
                            width={"full"}
                            onClick={async () => {
                              setIsOpenFeatureTypeDialog(false);
                              drawNewEntity();
                            }}
                          >
                            Draw
                          </Button>
                        </VStack>
                      </Popover.Body>
                    </Popover.Content>
                  </Popover.Positioner>
                </Portal>
              </Popover.Root>
            )}

            <Tooltip content={"Spline " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.SPLINE)}>
              <Button
                data-tool="spline-btn"
                width="45px"
                height="45px"
                colorPalette="gray"
                variant={activeTool === TOOL.ARC || activeArc ? "solid" : "outline"}
                disabled={lockedTo(TOOL.ARC) && activeTool !== TOOL.ARC}
                onClick={async () => {
                  if (activeTool === TOOL.NEW) cancelDrawing();

                  if (activeTool === TOOL.ARC || activeArc) {
                    if (arcPromiseRef.current?.cancel) arcPromiseRef.current.cancel();
                    arcPromiseRef.current = null;
                    setActiveArc(false);
                    setActiveTool(TOOL.NONE);
                    setEditType("none");
                    setCursorMode(Type.CURSOR.DEFAULT);
                    return;
                  }

                  if (!canStartTool(TOOL.ARC)) return;
                  await startQuadraticSplineOnce();
                }}
              >
                <LuSpline />
              </Button>
            </Tooltip>

            <Tooltip content="Cubic Spline (4-point)">
              <Button
                data-tool="spline3-btn"
                width="45px"
                height="45px"
                colorPalette="gray"
                variant={activeTool === TOOL.ARC_CUBIC || activeArc3 ? "solid" : "outline"}
                disabled={lockedTo(TOOL.ARC_CUBIC) && activeTool !== TOOL.ARC_CUBIC}
                onClick={async () => {
                  if (activeTool === TOOL.NEW) cancelDrawing();

                  if (activeTool === TOOL.ARC_CUBIC || activeArc3) {
                    if (arcPromiseRef.current?.cancel) arcPromiseRef.current.cancel();
                    arcPromiseRef.current = null;
                    setActiveArc3(false);
                    setActiveTool(TOOL.NONE);
                    setEditType("none");
                    setCursorMode(Type.CURSOR.DEFAULT);
                    return;
                  }

                  if (!canStartTool(TOOL.ARC_CUBIC)) return;
                  await startCubicSplineOnce();
                }}
              >
                <LuTangent />
              </Button>
            </Tooltip>

            {isPolygonLayer && (
              <Tooltip content="Box From Center Line">
                <Button
                  data-tool="box-btn"
                  width="45px"
                  height="45px"
                  colorPalette="gray"
                  variant={activeTool === TOOL.BOX || activeBox ? "solid" : "outline"}
                  disabled={lockedTo(TOOL.BOX) && activeTool !== TOOL.BOX}
                  onClick={async () => {
                    // toggle off
                    if (activeTool === TOOL.BOX || activeBox) {
                      if (boxPromiseRef.current?.cancel) boxPromiseRef.current.cancel();
                      boxPromiseRef.current = null;

                      setActiveBox(false);
                      setActiveTool(TOOL.NONE);
                      setEditType("none");
                      setCursorMode(Type.CURSOR.DEFAULT);

                      setIsOpenBoxWidthDialog(false);
                      setBoxCenterLine(null);
                      setBoxPreviewLength(0);

                      showToast("Box drawing cancelled", Type.NOTIFICATION.INFO);
                      return;
                    }

                    if (!canStartTool(TOOL.BOX)) return;

                    try {
                      setActiveTool(TOOL.BOX);
                      setActiveBox(true);
                      setCursorMode(Type.CURSOR.PLUS);
                      showToast("Select start and end points", Type.NOTIFICATION.INFO);

                      const p = drawBoxCenterLineOnce({
                        viewer: viewerRef.current,
                        dataSource: currentLayerEditing.dataSource,
                        onLength: (m) => setBoxPreviewLength(m),
                      });

                      boxPromiseRef.current = p;

                      const res = await p; // { start, end, lengthMeters }
                      boxPromiseRef.current = null;

                      setBoxCenterLine(res);
                      setBoxPreviewLength(res.lengthMeters || 0);
                      setIsOpenBoxWidthDialog(true);
                    } catch (e) {
                      boxPromiseRef.current = null;
                      if (e !== "cancelled") console.error(e);
                      setActiveBox(false);
                      setActiveTool(TOOL.NONE);
                      setCursorMode(Type.CURSOR.DEFAULT);
                    }
                  }}
                >
                  <LuRectangleHorizontal />
                </Button>
              </Tooltip>
            )}

            {isPolylineLayer && (
              <Popover.Root
                open={isOpenDigitizerDialog}
                onOpenChange={(e) => {
                  setIsOpenDigitizerDialog(e.open);
                }}
              >
                <Popover.Trigger asChild>
                  <Button
                    data-tool="digitizer-assistant-btn"
                    width="45px"
                    height="45px"
                    colorPalette="gray"
                    variant={activeTool === TOOL.DIGITIZER_ASSISTANT ? "solid" : "outline"}
                    onClick={() => setIsOpenDigitizerDialog(true)}
                  >
                    <Tooltip content="Digitizer Assistant">
                      <LuBot />
                    </Tooltip>
                  </Button>
                </Popover.Trigger>

                <Portal>
                  <Popover.Positioner>
                    <Popover.Content>
                      <Popover.Arrow />
                      <Popover.Body>
                        <VStack gap={1}>
                          <Heading width="full">Digitizer Assistant</Heading>

                          <Text width="full">Move Distance Min (m)</Text>
                          <NumberInput.Root
                            defaultValue={digitizerMinDistance}
                            width="full"
                            mb={2}
                            step={1}
                            onChange={(v) => {
                              const val = parseFloat(v.target.value);
                              setDigitizerMinDistance(val);
                              digitizerMinDistanceRef.current = val;
                            }}
                          >
                            <NumberInput.Control />
                            <NumberInput.Input />
                          </NumberInput.Root>

                          <Text width="full">Move Distance Max (m)</Text>
                          <NumberInput.Root
                            defaultValue={digitizerMaxDistance}
                            width="full"
                            mb={2}
                            step={1}
                            onChange={(v) => {
                              const val = parseFloat(v.target.value);
                              setDigitizerMaxDistance(val);
                              digitizerMaxDistanceRef.current = val;
                            }}
                          >
                            <NumberInput.Control />
                            <NumberInput.Input />
                          </NumberInput.Root>

                          <Button
                            width="full"
                            colorPalette={activeTool === TOOL.DIGITIZER_ASSISTANT ? "red" : "green"}
                            onClick={() => {
                              if (activeTool === TOOL.DIGITIZER_ASSISTANT) {
                                stopDigitizer();
                                setIsOpenDigitizerDialog(false);
                              } else {
                                startDigitizer();
                              }
                            }}
                          >
                            {activeTool === TOOL.DIGITIZER_ASSISTANT ? "Stop" : "Start"}
                          </Button>
                        </VStack>
                      </Popover.Body>
                    </Popover.Content>
                  </Popover.Positioner>
                </Portal>
              </Popover.Root>
            )}

            <Popover.Root
              open={isOpenParallelDialog}
              onOpenChange={(e) => {
                if (activeTool === TOOL.PARALLEL) return;
                setIsOpenParallelDialog(e.open);
              }}
            >
              <Popover.Trigger asChild>
                <Button
                  data-tool="parallel-btn"
                  width="45px"
                  height="45px"
                  colorPalette="gray"
                  variant={activeTool === TOOL.PARALLEL ? "solid" : "outline"}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Spacebar" || e.key === "Space") {
                      setIsOpenParallelDialog(false);
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  onClick={async () => {
                    if (activeTool === TOOL.PARALLEL) {
                      if (parallelPromiseRef.current?.cancel) {
                        parallelPromiseRef.current.cancel();
                      }

                      setIsOpenParallelDialog(false);
                      setActiveTool(TOOL.NONE);
                      setEditType("none");
                      setCursorMode(Type.CURSOR.DEFAULT);
                      showToast("Parallel drawing cancelled", Type.NOTIFICATION.INFO);
                      return;
                    }

                    setIsOpenParallelDialog(true);
                  }}
                >
                  <Tooltip content={"Constructing a parallel line " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.PARALLEL)}>
                    <HoParallelLine />
                  </Tooltip>
                </Button>
              </Popover.Trigger>

              <Portal>
                <Popover.Positioner>
                  <Popover.Content>
                    <Popover.Arrow />
                    <Popover.Body>
                      <VStack gap={1}>
                        <Heading width={"full"}>Add Parallel Feature</Heading>
                        <Text width={"full"}>Distance (m)</Text>
                        <NumberInput.Root
                          defaultValue={parallelDistance}
                          width="full"
                          mb={2}
                          step={0.1}
                          onChange={(v) => {
                            setParallelDistance(parseFloat(v.target.value));
                          }}
                        >
                          <NumberInput.Control />
                          <NumberInput.Input />
                        </NumberInput.Root>

                        <HStack gap={1} width={"full"} mb={2}>
                          <Checkbox.Root checked={parallelDirection === "left"} onCheckedChange={() => setParallelDirection("left")}>
                            <Checkbox.HiddenInput />
                            <Checkbox.Control />
                            <Checkbox.Label>Left</Checkbox.Label>
                          </Checkbox.Root>
                          <Checkbox.Root checked={parallelDirection === "right"} onCheckedChange={() => setParallelDirection("right")}>
                            <Checkbox.HiddenInput />
                            <Checkbox.Control />
                            <Checkbox.Label>Right</Checkbox.Label>
                          </Checkbox.Root>
                        </HStack>

                        <Button
                          width={"full"}
                          onClick={async () => {
                            if (!canStartTool(TOOL.PARALLEL)) return;

                            setIsOpenParallelDialog(false);
                            setActiveTool(TOOL.PARALLEL);

                            try {
                              showToast("Select lines (click) then press Enter", Type.NOTIFICATION.INFO);
                              parallelPromiseRef.current = offsetSelectedPolylineOnce({
                                viewer: viewerRef.current,
                                distanceMeters: parallelDistance,
                                direction: parallelDirection,
                                pushUndo,
                                defaultDataSource: currentLayerEditing.dataSource,
                              });

                              const payload = await parallelPromiseRef.current;

                              setNewGeojson(payload);

                              setOpenPropertiesDialog(true);
                              setEditType("none");
                            } catch (e) {
                              if (e === "cancelled") {
                                showToast("Parallel drawing cancelled", Type.NOTIFICATION.INFO);
                              } else {
                                console.error(e);
                              }
                            } finally {
                              parallelPromiseRef.current = null;
                              setActiveTool(TOOL.NONE);
                              setCursorMode(Type.CURSOR.DEFAULT);
                            }
                          }}
                        >
                          Select
                        </Button>
                      </VStack>
                    </Popover.Body>
                  </Popover.Content>
                </Popover.Positioner>
              </Portal>
            </Popover.Root>

            <Tooltip content={"Undo " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.UNDOEDIT)}>
              <Button data-tool="undo-btn" width="45px" ml={3} height="45px" colorPalette="gray" variant={"outline"} onClick={undoFunction}>
                <LuUndo />
              </Button>
            </Tooltip>

            <Tooltip content={"Redo " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.REDO)}>
              <Button data-tool="redo-btn" width="45px" height="45px" colorPalette="gray" variant={"outline"} onClick={redoFunction}>
                <LuRedo />
              </Button>
            </Tooltip>

            <Tooltip content={"Cancel Editing " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.CANCEL_EDITING)}>
              <Button data-tool="cancel-btn" width="45px" height="45px" colorPalette="red" variant="outline" ml={3} onClick={cancelEditing}>
                <LuX />
              </Button>
            </Tooltip>
          </ButtonGroup>
        </VStack>

        <LayersEditPropertiesPopup openProperties={[openPropertiesDialog, setOpenPropertiesDialog]} geojson={newGeojson} />

        <LayerEditConfirmCancelDialog state={[isShowConfirmCancelDialog, setIsShowConfirmCancelDialog]} setForceToCancel={setForceToCancel} />

        <Dialog.Root
          lazyMount
          open={isOpenBoxWidthDialog}
          onOpenChange={(e) => {
            setIsOpenBoxWidthDialog(e.open);
            if (!e.open) {
              setActiveBox(false);
              setActiveTool(TOOL.NONE);
              setCursorMode(Type.CURSOR.DEFAULT);
              setBoxCenterLine(null);
              setBoxPreviewLength(0);
            }
          }}
        >
          <Portal>
            <Dialog.Backdrop />
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header>
                  <Dialog.Title>Box Width</Dialog.Title>
                </Dialog.Header>

                <Dialog.Body>
                  <VStack alignItems="start" gap={3}>
                    <Text>Length: {Number(boxPreviewLength || 0).toFixed(2)} m</Text>

                    <Text width="full">Width (m)</Text>
                    <NumberInput.Root defaultValue={boxWidth} width="full" step={0.1} min={0.1} onChange={(v) => setBoxWidth(parseFloat(v.target.value))}>
                      <NumberInput.Control />
                      <NumberInput.Input />
                    </NumberInput.Root>
                  </VStack>
                </Dialog.Body>

                <Dialog.Footer>
                  <Dialog.ActionTrigger asChild>
                    <Button variant="outline">Cancel</Button>
                  </Dialog.ActionTrigger>

                  <Button
                    colorPalette="green"
                    onClick={() => {
                      if (!boxCenterLine?.start || !boxCenterLine?.end) return;

                      const out = buildBoxPolygonFromCenterLine({
                        start: boxCenterLine.start,
                        end: boxCenterLine.end,
                        widthMeters: boxWidth,
                      });

                      if (!out?.geojson) {
                        showToast("Invalid box geometry", Type.NOTIFICATION.ERROR);
                        return;
                      }

                      setIsOpenBoxWidthDialog(false);
                      setActiveBox(false);
                      setActiveTool(TOOL.NONE);
                      setCursorMode(Type.CURSOR.DEFAULT);

                      setNewGeojson(out.geojson);

                      setOpenPropertiesDialog(true);
                      setEditType("none");

                      setBoxCenterLine(null);
                      setBoxPreviewLength(0);
                    }}
                  >
                    Create
                  </Button>
                </Dialog.Footer>

                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>

        {undoStackRef.current.length > 0 && (
          <Dialog.Root
            lazyMount
            open={showApprovalEditDialog}
            onOpenChange={(e) => {
              setShowApprovalEditDialog(e.open);
            }}
          >
            <Portal>
              <Dialog.Backdrop />
              <Dialog.Positioner>
                <Dialog.Content>
                  <Dialog.Header>
                    <Dialog.Title> Cancel Confirmation </Dialog.Title>
                  </Dialog.Header>
                  <Dialog.Body>
                    <Text>Do you want to discard changes?</Text>
                  </Dialog.Body>
                  <Dialog.Footer>
                    <Dialog.ActionTrigger asChild>
                      <Button variant="outline">Cancel</Button>
                    </Dialog.ActionTrigger>
                    <Button
                      colorPalette={"red"}
                      onClick={() => {
                        cancelEditing();
                      }}
                    >
                      Cancel Editing
                    </Button>
                    <Button
                      colorPalette={"green"}
                      onClick={() => {
                        saveEditing();
                      }}
                    >
                      Save
                    </Button>
                  </Dialog.Footer>
                  <Dialog.CloseTrigger asChild>
                    <CloseButton size="sm" />
                  </Dialog.CloseTrigger>
                </Dialog.Content>
              </Dialog.Positioner>
            </Portal>
          </Dialog.Root>
        )}
      </Box>
    </Portal>
  );
};

export default LayersEdit;
